import { AIModule } from '@/data/ai/types';

export const DATA_PIPELINE_FUNDAMENTALS_MODULE: AIModule = {
  id: 'data-pipeline-fundamentals',
  title: 'Data Pipeline Fundamentals',
  description: 'Learn core concepts of data pipelines, ETL processes, and data engineering basics.',
  duration: '2 hours',
  difficulty: 'beginner',
  order: 1,
  content: `
## What You'll Learn
- Understand data pipeline architecture and components
- Master ETL (Extract, Transform, Load) processes
- Learn data quality and validation techniques
- Implement basic data pipelines with Python

## Overview
Data pipelines are the backbone of modern ML systems. They move data from sources to destinations while transforming and validating it along the way.

---

## Key Concepts

### 1. Pipeline Architecture
A data pipeline consists of three main stages:

\`\`\`python
# Basic ETL Pipeline Structure
class DataPipeline:
    def extract(self, source):
        """Extract data from source systems"""
        return pd.read_csv(source)
    
    def transform(self, data):
        """Apply transformations"""
        data = data.dropna()
        data['processed_at'] = datetime.now()
        return data
    
    def load(self, data, destination):
        """Load to destination"""
        data.to_parquet(destination)
\`\`\`

### 2. Data Quality Checks
Always validate data at each stage:

\`\`\`python
def validate_data(df):
    checks = {
        'no_nulls': df.isnull().sum().sum() == 0,
        'row_count': len(df) > 0,
        'schema_valid': set(df.columns) == EXPECTED_COLS
    }
    return all(checks.values())
\`\`\`

---

## Best Practices
- **Idempotency**: Pipelines should produce same results when re-run
- **Logging**: Log every stage for debugging
- **Error Handling**: Implement retry logic with exponential backoff
- **Monitoring**: Track data quality metrics over time

---

## Key Takeaways
✅ Data pipelines move and transform data reliably
✅ ETL is the foundation of data engineering
✅ Always validate data at each pipeline stage
✅ Design for idempotency and fault tolerance

## Resources
- [Apache Airflow Documentation](https://airflow.apache.org/)
- [Data Engineering Cookbook](https://github.com/andkret/Cookbook)
`,
  learningObjectives: [
    'Understand data pipeline architecture',
    'Implement ETL processes',
    'Apply data quality validation',
    'Design fault-tolerant pipelines'
  ],
  quiz: {
    questions: [
      {
        id: 'dpf-q1',
        question: 'What does ETL stand for?',
        options: ['Extract, Transform, Load', 'Enter, Test, Launch', 'Export, Transfer, Link', 'Evaluate, Train, Learn'],
        correctAnswer: 0,
        explanation: 'ETL stands for Extract, Transform, Load - the three main stages of a data pipeline.'
      },
      {
        id: 'dpf-q2',
        question: 'Why is idempotency important in data pipelines?',
        options: ['For faster processing', 'To reduce costs', 'To ensure consistent results on re-runs', 'For better visualization'],
        correctAnswer: 2,
        explanation: 'Idempotent pipelines produce the same results when re-run, making them reliable and predictable.'
      },
      {
        id: 'dpf-q3',
        question: 'Which stage validates data quality?',
        options: ['Only Extract', 'Only Load', 'All stages', 'None'],
        correctAnswer: 2,
        explanation: 'Data quality should be validated at all stages of the pipeline to catch issues early.'
      },
      {
        id: 'dpf-q4',
        question: 'What is the main purpose of logging in pipelines?',
        options: ['Performance optimization', 'Debugging and monitoring', 'Data compression', 'Schema validation'],
        correctAnswer: 1,
        explanation: 'Logging helps track pipeline execution and debug issues when they occur.'
      },
      {
        id: 'dpf-q5',
        question: 'Which format is commonly used for storing processed data?',
        options: ['CSV only', 'Parquet', 'Plain text', 'Images'],
        correctAnswer: 1,
        explanation: 'Parquet is a columnar format optimized for analytics and is commonly used in data pipelines.'
      }
    ]
  },
  practice: {
    task: 'Build a Basic ETL Pipeline: Create a simple ETL pipeline that extracts data from CSV, transforms it, and loads to Parquet. Implement extract(), transform(), and load() methods for a sales data pipeline.',
    starter: `import pandas as pd
from datetime import datetime

class SalesPipeline:
    def extract(self, filepath):
        # TODO: Read CSV file
        pass
    
    def transform(self, data):
        # TODO: Clean nulls and add processed_at column
        pass
    
    def load(self, data, output_path):
        # TODO: Save to parquet
        pass

# Test your pipeline
pipeline = SalesPipeline()`,
    solution: `import pandas as pd
from datetime import datetime

class SalesPipeline:
    def extract(self, filepath):
        return pd.read_csv(filepath)
    
    def transform(self, data):
        data = data.dropna()
        data['processed_at'] = datetime.now()
        data['total'] = data['quantity'] * data['price']
        return data
    
    def load(self, data, output_path):
        data.to_parquet(output_path, index=False)
        print(f"Saved {len(data)} rows to {output_path}")

# Test your pipeline
pipeline = SalesPipeline()
data = pipeline.extract('sales.csv')
transformed = pipeline.transform(data)
pipeline.load(transformed, 'sales_processed.parquet')`,
    hints: [
      'Use pd.read_csv() for extraction',
      'Use dropna() to remove null values',
      'Use to_parquet() for loading'
    ],
    estimatedTime: 20
  }
};
